import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { map, Observable, of } from 'rxjs';
import { ICON } from '@shared/constants/icons.constant';
import { hasUnsupportedScope, SearchConnector, SearchResultItem, SearchScope } from '@shared/global-search';
import { Icon } from '@shared/types/icon.type';
import { Property } from '../../../shared/models/entity/property/property.model';
import { PropertyFilter } from '../../../shared/models/entity/property/property-filter.model';
import { PropertiesService } from '../../../shared/services/entity/properties.service';

const MAX_RESULTS = 6;

/** Searches the Objekte by name, number and address. */
@Injectable({ providedIn: 'root' })
export class PropertiesSearchConnector implements SearchConnector {
    // -----------------------------------------------------------------------------------------------------
    // @ DEPENDENCIES
    // -----------------------------------------------------------------------------------------------------

    private readonly entityService = inject(PropertiesService);

    private readonly router = inject(Router);

    // -----------------------------------------------------------------------------------------------------
    // @ INSTANCE VARIABLES
    // -----------------------------------------------------------------------------------------------------

    readonly id = 'properties';

    readonly label = 'Objekte';

    readonly order = 20;

    readonly icon: Icon = ICON.buildings;

    // -----------------------------------------------------------------------------------------------------
    // @ PUBLIC METHODS
    // -----------------------------------------------------------------------------------------------------

    search(query: string, scope: SearchScope): Observable<SearchResultItem[]> {
        // Properties cannot be narrowed by any entity scope — a scoped palette skips them
        if (hasUnsupportedScope(scope, [])) {
            return of([]);
        }

        return this.entityService
            .getAll({
                filter: { filter: query } as PropertyFilter,
                pagination: { page: 1, pageSize: MAX_RESULTS },
            })
            .pipe(map((items) => items.map((item) => this.toResultItem(item))));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ PRIVATE METHODS
    // -----------------------------------------------------------------------------------------------------

    private toResultItem(item: Property): SearchResultItem {
        return {
            id: item.id,
            title: `${String(item.number).padStart(3, '0')} — ${item.name}`,
            subtitle: [item.street, item.city].filter(Boolean).join(', '),
            icon: this.icon,
            payload: item,
            actions: [
                {
                    id: 'open',
                    label: 'Öffnen',
                    icon: ICON.caretRight,
                    execute: () => {
                        this.router.navigate(['/properties', item.id]);
                    },
                },
                {
                    id: 'scope',
                    label: 'Als Suchfilter verwenden',
                    icon: ICON.funnel,
                    keepOpen: true,
                    execute: (_, context) => {
                        context.setScope('propertyId', item.id, `Objekt: ${item.name}`);
                    },
                },
            ],
        };
    }
}
